import { matches, matchingGroup, waitMs, type MatchContext, type VisitorTrigger } from '@shared/triggers';

export interface SessionStore {
    get(key: string): string | null;
    set(key: string, value: string | null): void;
}

export interface TriggerRunnerOptions {
    /** Highest priority first. */
    triggers: VisitorTrigger[];
    /** Fresh facts about the page and visitor, read on every check. */
    context: () => MatchContext;
    /** Built-in guards (messenger closed, no conversation going, no bubble showing). */
    canFire: () => boolean;
    fire: (trigger: VisitorTrigger) => void;
    /** Remembers what fired this browsing session (sessionStorage). */
    session: SessionStore;
    sessionKey: string;
    /** Dashboard playground: frequency is ignored so a trigger can be tried again and again. */
    test?: boolean;
}

/** Slack for timers firing a little early against time-on-site rules. */
const TIME_SLACK_MS = 250;

/**
 * Decides when an automatic message appears. On each page (load or in-app
 * navigation) it picks the first trigger whose conditions match, waits its
 * delay, checks again, and fires it. At most one trigger fires per page.
 */
export class TriggerRunner {
    private timer: ReturnType<typeof setTimeout> | undefined;
    private page: string | null = null;
    private firedOnPage = false;

    constructor(private options: TriggerRunnerOptions) {}

    /** Call on boot and whenever the URL may have changed; a hash change alone isn't a new page. */
    evaluate() {
        const page = this.options.context().url.split('#')[0]!;
        if (page === this.page) return;

        this.page = page;
        this.firedOnPage = false;
        this.schedule();
    }

    stop() {
        clearTimeout(this.timer);
        this.timer = undefined;
    }

    private schedule() {
        this.stop();
        const ctx = this.options.context();

        for (const trigger of this.options.triggers) {
            const group = this.spent(trigger) ? undefined : matchingGroup(trigger, ctx, { ignoreTime: true });
            if (!group) continue;

            const hasSiteTime = group.rules.some((rule) => rule.type === 'time_on_site');
            this.timer = setTimeout(
                () => this.attempt(trigger),
                waitMs(trigger, group, ctx) + (hasSiteTime ? TIME_SLACK_MS : 0),
            );
            return;
        }
    }

    private attempt(trigger: VisitorTrigger) {
        this.timer = undefined;

        // The visitor may have navigated or changed since the timer started.
        if (this.firedOnPage || this.spent(trigger) || !matches(trigger, this.options.context())) return;
        if (!this.options.canFire()) return;

        this.firedOnPage = true;
        this.markSpent(trigger);
        this.options.fire(trigger);
    }

    private fired(): string[] {
        try {
            const value = JSON.parse(this.options.session.get(this.options.sessionKey) ?? '[]');
            return Array.isArray(value) ? value : [];
        } catch {
            return [];
        }
    }

    /**
     * Once-per-visitor triggers are also left out by the server on the next
     * boot; remembering them here covers the moments before it records them.
     */
    private spent(trigger: VisitorTrigger) {
        return !this.options.test && trigger.frequency !== 'every_time' && this.fired().includes(trigger.id);
    }

    private markSpent(trigger: VisitorTrigger) {
        if (this.options.test || trigger.frequency === 'every_time') return;
        this.options.session.set(this.options.sessionKey, JSON.stringify([...this.fired(), trigger.id].slice(-100)));
    }
}
